import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { Employee, AuthUser } from '@/types/auth';
import { DEFAULT_USER_ROLE, DEFAULT_DEPARTMENT, DEFAULT_STATUS } from '@/constants';

export const createDefaultUser = (supabaseUser: User): AuthUser => ({
  id: supabaseUser.id,
  username: supabaseUser.user_metadata?.full_name || 
            supabaseUser.user_metadata?.display_name || 
            supabaseUser.email || 
            'مستخدم جديد',
  role: DEFAULT_USER_ROLE,
  permissions: ['الرئيسية'], // Give basic dashboard access by default
  isSupabaseUser: true
});

export const createAuthUserFromEmployee = async (employee: Employee): Promise<AuthUser> => {
  try {
    console.log('Getting permissions for employee:', employee.id, employee.name, employee.email);
    
    // التحقق من مدراء النظام الرئيسيين - بدون استدعاء قاعدة البيانات
    // نتحقق من الإيميل بعدة طرق للتأكد من عدم فقدان الصلاحيات
    const isSystemAdmin = 
      employee.email === 'klidmorre@gmail.com' || 
      employee.email === 'eng.khalid.work@gmail.com' ||
      employee.position === 'مدير النظام' ||
      employee.position === 'مدير النظام العام' ||
      employee.position === 'مدير النظام الرئيسي';
    
    if (isSystemAdmin) {
      console.log(`System admin (${employee.email}) detected, granting ALL permissions`);
      return {
        id: employee.id,
        username: employee.name,
        role: 'مدير النظام العام',
        permissions: 'ALL',
        isSupabaseUser: true,
        employeeData: {
          ...employee,
          position: employee.position || 'مدير النظام العام',
          approval_status: 'approved',
          status: 'present' // تأكد من أن الحالة موجودة دائماً
        }
      };
    }
    
    // Get user permissions from database based on assigned roles
    const { data: permissions, error } = await supabase.rpc('get_user_permissions', {
      emp_id: employee.id
    });

    if (error) {
      console.error('Error calling get_user_permissions:', error);
      throw error;
    }

    console.log('Employee permissions received:', permissions);

    // Check if permissions is an array or contains ALL
    let finalPermissions: string[] | 'ALL' = permissions || [];
    
    if (Array.isArray(permissions) && permissions.includes('ALL')) {
      finalPermissions = 'ALL';
    } else if (!Array.isArray(permissions)) {
      finalPermissions = [];
    }

    return {
      id: employee.id,
      username: employee.name,
      role: employee.position || DEFAULT_USER_ROLE,
      permissions: finalPermissions,
      isSupabaseUser: true,
      employeeData: employee
    };
  } catch (error) {
    console.error('Error getting user permissions:', error);
    
    // تحقق من حالة الموافقة قبل إعطاء صلاحيات افتراضية
    if (employee.approval_status !== 'approved') {
      return {
        id: employee.id,
        username: employee.name,
        role: employee.position || DEFAULT_USER_ROLE,
        permissions: ['الملف الشخصي'], // صلاحيات محدودة للموظفين غير المعتمدين
        isSupabaseUser: true,
        employeeData: employee
      };
    }
    
    // Fallback to basic permissions for approved employees
    return {
      id: employee.id,
      username: employee.name,
      role: employee.position || DEFAULT_USER_ROLE,
      permissions: ['الرئيسية'],
      isSupabaseUser: true,
      employeeData: employee
    };
  }
};

export const ensureEmployeeRecord = async (user: User): Promise<Employee | null> => {
  try {
    console.log(`Checking employee record for user ${user.email}`);
    
    // البحث المباشر عن الموظف
    const { data: employee, error } = await supabase
      .from('employees')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Database error while fetching employee:', error);
      return null;
    }
    
    if (employee) {
      console.log('Employee record found:', employee.name);
      return employee;
    }

    console.log('Creating employee record...');
    const { data: newEmployee, error: insertError } = await supabase
      .from('employees')
      .insert({
        user_id: user.id,
        name: user.user_metadata?.full_name || 
              user.user_metadata?.display_name || 
              user.email || 
              'مستخدم جديد',
        email: user.email,
        position: DEFAULT_USER_ROLE,
        department: DEFAULT_DEPARTMENT,
        status: DEFAULT_STATUS,
        join_date: new Date().toISOString().split('T')[0],
        hiring_date: new Date().toISOString().split('T')[0]
      })
      .select()
      .maybeSingle();
      
    if (newEmployee && !insertError) {
      console.log('Employee record created successfully:', newEmployee.name);
      return newEmployee;
    } else {
      console.error('Failed to create employee record:', insertError);
    }
  } catch (err) {
    console.error('Error in ensureEmployeeRecord:', err);
  }
  
  return null;
};

export const createTimeoutPromise = (ms: number, message: string) => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(message)), ms);
  });
};